import { all, call, delay, put, race, select, take, takeLatest } from 'redux-saga/effects';
import { AppStateType } from '~/store/modules/root-reducer';
import { ISA_ERROR, USER_ROLE } from '~/constants';
import notification from '~/services/notification';
import { formatMessage } from '~/i18n/translate';
import history from '~/services/history';

import * as routingActions from './routing-actions';
import routingService from './routing-service';
import { LOGOUT_REQUEST, SET_USER_SELECTED_FOR_CONTEXT } from '~/store/modules/auth/auth-constants';
import { selectEnableAIRoutingSuggestion } from '~/store/modules/configuration/configuration-selectors';
import {
  AI_ROUTE_JOB_POLLING_INTERVAL,
  AI_ROUTE_JOB_STORAGE_KEY,
  CLEAR_AI_ROUTE_JOB,
  STOP_AI_ROUTE_JOB_POLLING,
  RESUME_AI_ROUTE_JOB_POLLING,
  DELETE_ROUTE_REQUEST,
  EDIT_ROUTE_REQUEST,
  GENERATE_AI_ROUTES_REQUEST,
  LOAD_GENERATE_AI_ROUTE_REQUEST,
  LOAD_GENERATE_AI_ROUTES_LIST_REQUEST,
  LOAD_INITIAL_DATA_REQUEST,
  LOAD_ROUTES_REQUEST,
  RESTORE_AI_ROUTE_JOB,
  ROUTING_LIST,
  SAVE_AI_ROUTE_SETTINGS_REQUEST,
  SAVE_ROUTE_REQUEST,
  SET_PAGE,
  SET_ROWS_PER_PAGE,
  START_AI_ROUTE_JOB
} from './routing-constants';
import {
  AIRouteJob,
  AIRouteJobStatus,
  RouteAllocation,
  RoutingFilters,
  RoutingListType,
  VendorClientRoutePayload
} from './routing-interfaces';
import { UserInterface } from '@e3labs/isa-data-dictionary';

export function* loadInitialData({ filters, id, month, year, routeType }: any) {
  try {
    yield put(routingActions.setLoadingClients(true));
    yield put(routingActions.setWalletSummary(null));

    const user = yield select((state: AppStateType) => state.auth.user);

    let effectiveFilters = filters;
    let existingAllocations: RouteAllocation[] | undefined;

    let routeClientExternalCodes: string | undefined;
    let routeSellerExternalCode: string | undefined;
    let routeClientsCount = 50;

    if (id) {
      const { data: route } = yield call(
        [routingService, routingService.getRoutesByUserExternalCode],
        id,
        month,
        year,
        routeType
      );

      const allRoutes = Array.isArray(route) ? route : route ? [route] : [];
      const routeArray = routeType ? allRoutes.filter((r: any) => !r.type || r.type === routeType) : allRoutes;

      if (routeArray.length > 0) {
        existingAllocations = routeArray.map((r: any) => ({
          clientExternalCode: r.clientExternalCode,
          weekNumber: r.weekNumber,
          dayOfWeek: Array.isArray(r.dayOfWeek) ? r.dayOfWeek[0] : r.dayOfWeek,
          periodicity: r.periodicity,
          visitOrder: r.visitOrder
        }));

        const uniqueCodes = [...new Set(routeArray.map((r: any) => r.clientExternalCode as string))];
        routeClientExternalCodes = uniqueCodes.join(',');
        routeClientsCount = uniqueCodes.length;

        const sellerExternalCode: string = routeArray[0].userExternalCode;
        routeSellerExternalCode = sellerExternalCode;

        if (sellerExternalCode) {
          let seller: UserInterface | undefined;
          if (user?.userRole === USER_ROLE.VENDOR) {
            seller = {
              ...user,
              externalCode: user?.externalCode
            };
          }

          if (user?.userRole !== USER_ROLE.VENDOR) {
            const { data: sellers } = yield call([routingService, routingService.getSellers], {
              userExternalCodes: user?.userRole !== USER_ROLE.VENDOR ? sellerExternalCode : user?.externalCode
            });
            seller = sellers?.find((s: any) => s.externalCode === sellerExternalCode) ?? {
              externalCode: sellerExternalCode
            };
          }

          effectiveFilters = { ...filters, seller };
          yield put(routingActions.setFilters(effectiveFilters));

          try {
            const { data: walletData } = yield call(
              [routingService, routingService.getWalletRouteSummary],
              sellerExternalCode
            );
            yield put(routingActions.setWalletSummary(walletData));
          } catch {
            yield put(routingActions.setWalletSummary(null));
          }
        }
      }
    }

    const requests: any[] = [
      call([routingService, routingService.getClientsBySeller], effectiveFilters),
      call([routingService, routingService.getCityLocales], effectiveFilters)
    ];

    if (routeClientExternalCodes) {
      requests.push(
        call(
          [routingService, routingService.getClientsByExternalCodes],
          routeClientExternalCodes,
          routeSellerExternalCode,
          routeClientsCount
        )
      );
    }

    const [{ data: sellerClients }, { data: cityLocales }, routeClientsResult] = yield all(requests);

    const routeClients: any[] = routeClientsResult?.data ?? [];
    const sellerClientCodes = new Set((sellerClients ?? []).map((c: any) => c.externalCode));
    const missingClients = routeClients.filter((c: any) => !sellerClientCodes.has(c.externalCode));
    const clients = [...(sellerClients ?? []), ...missingClients];

    yield put(
      routingActions.loadInitialDataSuccess({
        clients,
        cityLocales,
        allocations: existingAllocations
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(routingActions.setLoadingClients(false));
  }
}

export function* saveRoute({ allocations }: any) {
  try {
    yield put(routingActions.setLoadingSave(true));

    const filters: RoutingFilters = yield select((state: AppStateType) => state.routing.filters);
    const userExternalCode = filters.seller?.externalCode;

    if (!userExternalCode) {
      yield call(notification.warning, formatMessage('routingSelectVendorRequired'));
      return;
    }

    const payload: VendorClientRoutePayload[] = allocations.map((alloc: RouteAllocation) => ({
      clientExternalCode: alloc.clientExternalCode,
      userExternalCode,
      weekNumber: alloc.weekNumber,
      periodicity: alloc.periodicity,
      dayOfWeek: [alloc.dayOfWeek],
      visitOrder: alloc.visitOrder,
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear()
    }));

    yield call([routingService, routingService.saveAllocations], payload);
    yield call(notification.success, formatMessage('routingSuccessSave'));

    yield call(history.push, `/register/routing/list`);
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(routingActions.setLoadingSave(false));
  }
}

export function* editRoute({ allocations, id }: any) {
  try {
    yield put(routingActions.setLoadingSave(true));

    const userExternalCode = id;

    const payload: VendorClientRoutePayload[] = allocations.map((alloc: RouteAllocation) => ({
      clientExternalCode: alloc.clientExternalCode,
      userExternalCode,
      weekNumber: alloc.weekNumber,
      periodicity: alloc.periodicity,
      dayOfWeek: [alloc.dayOfWeek],
      visitOrder: alloc.visitOrder,
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear()
    }));

    yield call([routingService, routingService.updateRoute], payload);
    yield call(notification.success, formatMessage('routingSuccessSave'));

    yield call(history.push, `/register/routing/list`);
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(routingActions.setLoadingSave(false));
  }
}

export function* deleteRoute({ userExternalCode, userName }: any) {
  try {
    yield put(routingActions.setLoadingSave(true));

    const { isConfirmed } = yield call(
      notification.confirm,
      formatMessage('commonLabelExclude'),
      formatMessage('routingDeleteVendorConfirmation', { name: userName })
    );
    if (isConfirmed) {
      yield put(routingActions.setLoadingSave(true));
      yield call(routingService.deleteRoute, userExternalCode);
      yield call(notification.success, formatMessage('routingSuccessDelete'));

      const filters: RoutingFilters = yield select((state: AppStateType) => state.routing.filters);
      yield put(routingActions.loadRoutesRequest(filters));

      yield call(history.push, `/register/routing/list`);
    }
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(routingActions.setLoadingSave(false));
  }
}

const toCount = (raw: any): number => Number(typeof raw === 'number' ? raw : raw?.count ?? 0) || 0;

function* fetchManualRoutes(params: Record<string, any>) {
  const [{ data }, { data: count }] = yield all([
    call([routingService, routingService.getRoutesList], params),
    call([routingService, routingService.getRoutesList], { ...params, isCount: true })
  ]);

  return { data: data || [], count };
}

function* fetchAIRoutes(params: Record<string, any>) {
  const { skip, limit, userExternalCode, weekNumber, month, year } = params;
  const aiFilter = { userExternalCodes: userExternalCode, weekNumber, month, year };

  const [{ data }, { data: count }] = yield all([
    call([routingService, routingService.getGenerateAIRoutesList], { ...aiFilter, skip, limit }),
    call([routingService, routingService.getGenerateAIRoutesCount], aiFilter)
  ]);

  return { data: data || [], count };
}

export function* loadRoutes({ filters }: any) {
  try {
    yield put(routingActions.setLoadingClients(true));

    const rowsPerPage: number = yield select((state: AppStateType) => state.routing.list.rowsPerPage);
    const page: number = yield select((state: AppStateType) => state.routing.list.page);
    const { seller, month, skip: skipOverride, limit: limitOverride } = (filters as any) || {};
    const skip = skipOverride !== undefined ? skipOverride : page * rowsPerPage;
    const limit = limitOverride !== undefined ? limitOverride : rowsPerPage;
    const params: Record<string, any> = { skip, limit, year: new Date().getFullYear() };

    if (seller?.externalCode) params.userExternalCode = seller.externalCode;
    if (month != null) params.month = Number(month);

    const MONTH_NAMES: Record<number, string> = {
      1: 'Janeiro',
      2: 'Fevereiro',
      3: 'Março',
      4: 'Abril',
      5: 'Maio',
      6: 'Junho',
      7: 'Julho',
      8: 'Agosto',
      9: 'Setembro',
      10: 'Outubro',
      11: 'Novembro',
      12: 'Dezembro'
    };

    const enableAI: boolean = yield select(selectEnableAIRoutingSuggestion);
    const listType: RoutingListType = yield select((state: AppStateType) => state.routing.listType);
    const { data, count } =
      enableAI && listType === ROUTING_LIST.AI
        ? yield call(fetchAIRoutes, params)
        : yield call(fetchManualRoutes, params);

    const enriched = (data || []).map((row: any) => ({
      ...row,
      monthName: row.month ? MONTH_NAMES[row.month] ?? String(row.month) : '—'
    }));

    yield put(routingActions.loadRoutesSuccess({ data: enriched, count: toCount(count) }));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(routingActions.setLoadingClients(false));
  }
}

function* handlePageChange() {
  const filters: RoutingFilters = yield select((state: AppStateType) => state.routing.filters);
  yield put(routingActions.loadRoutesRequest(filters));
}

function* handleRowsPerPageChange() {
  const filters: RoutingFilters = yield select((state: AppStateType) => state.routing.filters);
  yield put(routingActions.loadRoutesRequest(filters));
}

export function* generateAIRoutes({ payload }: any) {
  try {
    const enableAI: boolean = yield select(selectEnableAIRoutingSuggestion);
    if (!enableAI) return;

    yield put(routingActions.setLoadingSave(true));
    yield put(routingActions.setLoadingAiSettings(true));

    if (!payload?.userExternalCode) {
      yield call(notification.warning, formatMessage('routingSelectVendorRequired'));
      return;
    }

    const { data } = yield call([routingService, routingService.postGenerateAIRoutes], payload);

    yield put(routingActions.generateAIRoutesSuccess(data));

    const jobId = data?._id;
    if (jobId) {
      const ownerId: string | null = yield select(selectAIRouteJobOwnerId);
      yield put(
        routingActions.startAIRouteJob({
          id: String(jobId),
          userExternalCode: payload.userExternalCode,
          ownerId,
          status: normalizeAIRouteJobStatus(data?.state ?? data?.status),
          queuePosition: data?.queuePosition ?? data?.position ?? null,
          createdAt: data?.execution_date ?? new Date().toISOString(),
          lastCheckedAt: null,
          data
        })
      );
      yield call(notification.success, formatMessage('routingAiJobQueued'));
    }

    yield call(history.push, `/register/routing/list`);
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(routingActions.setLoadingSave(false));
    yield put(routingActions.setLoadingAiSettings(false));
  }
}

export function* saveAIRouteSettings({ payload }: any) {
  try {
    const enableAI: boolean = yield select(selectEnableAIRoutingSuggestion);
    if (!enableAI) return;

    yield put(routingActions.setLoadingSave(true));
    yield put(routingActions.setLoadingAiSettings(true));

    if (!payload?.userExternalCode) {
      yield call(notification.warning, formatMessage('routingSelectVendorRequired'));
      return;
    }

    yield call([routingService, routingService.postSaveAIRouteSettings], payload);

    yield put(routingActions.saveAIRouteSettingsSuccess(payload.userExternalCode));
    yield call(notification.success, formatMessage('routingAiSettingsSaveSuccess'));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(routingActions.setLoadingSave(false));
    yield put(routingActions.setLoadingAiSettings(false));
  }
}

export function* loadGenerateAIRoutesList({ params }: any) {
  try {
    const enableAI: boolean = yield select(selectEnableAIRoutingSuggestion);
    if (!enableAI) return;

    yield put(routingActions.setLoadingClients(true));

    const { userExternalCodes, limit, skip } = params || {};

    const [{ data }, { data: count }] = yield all([
      call([routingService, routingService.getGenerateAIRoutesList], { userExternalCodes, limit, skip }),
      call([routingService, routingService.getGenerateAIRoutesCount], { userExternalCodes })
    ]);

    yield put(routingActions.loadGenerateAIRoutesListSuccess({ data, count: count?.count }));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(routingActions.setLoadingClients(false));
  }
}

export function* loadGenerateAIRoute({ id }: any) {
  try {
    const enableAI: boolean = yield select(selectEnableAIRoutingSuggestion);
    if (!enableAI) return;

    yield put(routingActions.setLoadingClients(true));

    const { data } = yield call([routingService, routingService.getGenerateAIRouteById], id);

    yield put(routingActions.loadGenerateAIRouteSuccess(data));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(routingActions.setLoadingClients(false));
  }
}

const AI_ROUTE_JOB_SESSION_END_ACTIONS = [LOGOUT_REQUEST, SET_USER_SELECTED_FOR_CONTEXT];

const selectAIRouteJobOwnerId = (state: AppStateType): string | null => {
  const user: any = state.auth.user;
  return user?.externalCode ?? user?._id ?? null;
};

const AI_ROUTE_JOB_TERMINAL_STATUSES: AIRouteJobStatus[] = ['completed', 'failed'];

export function normalizeAIRouteJobStatus(rawStatus?: string): AIRouteJobStatus {
  const status = String(rawStatus ?? '').toLowerCase();

  if (['completed', 'complete', 'done', 'finished', 'success', 'processed'].includes(status)) return 'completed';
  if (['failed', 'error', 'canceled', 'cancelled'].includes(status)) return 'failed';
  if (['processing', 'running', 'in_progress', 'in-progress', 'started', 'restarting', 'up_for_retry'].includes(status))
    return 'processing';

  return 'queued';
}

function persistAIRouteJob(job: AIRouteJob | null) {
  try {
    if (job) localStorage.setItem(AI_ROUTE_JOB_STORAGE_KEY, JSON.stringify(job));
    else localStorage.removeItem(AI_ROUTE_JOB_STORAGE_KEY);
  } catch {
    // storage indisponível: o job continua apenas em memória
  }
}

function readPersistedAIRouteJob(): AIRouteJob | null {
  try {
    const stored = localStorage.getItem(AI_ROUTE_JOB_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function* checkAIRouteJobStatus(id: string) {
  try {
    const { data } = yield call([routingService, routingService.getGenerateAIRouteStatus], id);

    const status = normalizeAIRouteJobStatus(data?.state ?? data?.status);
    yield put(
      routingActions.updateAIRouteJob({
        status,
        queuePosition: data?.queuePosition ?? data?.position ?? null,
        lastCheckedAt: new Date().toISOString(),
        data
      })
    );

    const job: AIRouteJob = yield select((state: AppStateType) => state.routing.aiRouteJob);
    persistAIRouteJob(job);

    return status;
  } catch (error) {
    return null;
  }
}

function* pollAIRouteJobLoop(id: string, checkImmediately: boolean) {
  if (!checkImmediately) yield delay(AI_ROUTE_JOB_POLLING_INTERVAL);

  while (true) {
    const status: AIRouteJobStatus | null = yield call(checkAIRouteJobStatus, id);

    if (status === 'completed') {
      yield call(notification.success, formatMessage('routingAiJobCompleted'));
      return;
    }

    if (status === 'failed') {
      yield call(notification.error, formatMessage('routingAiJobFailed'));
      return;
    }

    yield delay(AI_ROUTE_JOB_POLLING_INTERVAL);
  }
}

export function* watchAIRouteJob({ job, restored }: any) {
  const enableAI: boolean = yield select(selectEnableAIRoutingSuggestion);
  if (!enableAI) return;

  persistAIRouteJob(job);

  if (AI_ROUTE_JOB_TERMINAL_STATUSES.includes(job.status) || job.pollingStopped) return;

  yield race({
    polling: call(pollAIRouteJobLoop, job.id, !!restored),
    cancel: take([CLEAR_AI_ROUTE_JOB, STOP_AI_ROUTE_JOB_POLLING, ...AI_ROUTE_JOB_SESSION_END_ACTIONS])
  });
}

export function* stopAIRouteJobPolling() {
  yield put(routingActions.updateAIRouteJob({ pollingStopped: true }));

  const job: AIRouteJob | null = yield select((state: AppStateType) => state.routing.aiRouteJob);
  persistAIRouteJob(job);
}

export function* resumeAIRouteJobPolling() {
  const job: AIRouteJob | null = yield select((state: AppStateType) => state.routing.aiRouteJob);
  if (!job) return;

  yield put({ ...routingActions.startAIRouteJob({ ...job, pollingStopped: false }), restored: true });
}

export function* restoreAIRouteJob() {
  const enableAI: boolean = yield select(selectEnableAIRoutingSuggestion);
  if (!enableAI) return;

  const currentJob: AIRouteJob | null = yield select((state: AppStateType) => state.routing.aiRouteJob);
  if (currentJob) return;

  const job = readPersistedAIRouteJob();
  if (!job?.id) return;

  const ownerId: string | null = yield select(selectAIRouteJobOwnerId);
  if (!job.ownerId || job.ownerId !== ownerId) {
    yield call(persistAIRouteJob, null);
    return;
  }

  yield put({ ...routingActions.startAIRouteJob(job), restored: true });
}

export function* clearAIRouteJob() {
  yield call(persistAIRouteJob, null);
}

export function* clearAIRouteJobOnSessionEnd() {
  yield put(routingActions.clearAIRouteJob());
}

export default all([
  takeLatest(LOAD_INITIAL_DATA_REQUEST, loadInitialData),
  takeLatest(LOAD_ROUTES_REQUEST, loadRoutes),
  takeLatest(SET_PAGE, handlePageChange),
  takeLatest(SET_ROWS_PER_PAGE, handleRowsPerPageChange),
  takeLatest(SAVE_ROUTE_REQUEST, saveRoute),
  takeLatest(EDIT_ROUTE_REQUEST, editRoute),
  takeLatest(DELETE_ROUTE_REQUEST, deleteRoute),
  takeLatest(GENERATE_AI_ROUTES_REQUEST, generateAIRoutes),
  takeLatest(SAVE_AI_ROUTE_SETTINGS_REQUEST, saveAIRouteSettings),
  takeLatest(LOAD_GENERATE_AI_ROUTES_LIST_REQUEST, loadGenerateAIRoutesList),
  takeLatest(LOAD_GENERATE_AI_ROUTE_REQUEST, loadGenerateAIRoute),
  takeLatest(START_AI_ROUTE_JOB, watchAIRouteJob),
  takeLatest(RESTORE_AI_ROUTE_JOB, restoreAIRouteJob),
  takeLatest(CLEAR_AI_ROUTE_JOB, clearAIRouteJob),
  takeLatest(STOP_AI_ROUTE_JOB_POLLING, stopAIRouteJobPolling),
  takeLatest(RESUME_AI_ROUTE_JOB_POLLING, resumeAIRouteJobPolling),
  takeLatest(AI_ROUTE_JOB_SESSION_END_ACTIONS, clearAIRouteJobOnSessionEnd)
]);
